import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Changes that commit after a countdown unless undone, for an inline
 * "Undo 5" in a table row. Scheduling the same key again restarts it. Leaving
 * the page commits what is still pending, so a change the user made isn't
 * silently dropped.
 */

export interface PendingChange<V> {
  value: V;
  secondsLeft: number;
}

export interface UseUndoableChangesOptions<K, V> {
  onCommit: (key: K, value: V) => void;
  seconds?: number;
}

interface Timer<V> {
  value: V;
  remaining: number;
  interval: ReturnType<typeof setInterval>;
}

export function useUndoableChanges<K, V>({ onCommit, seconds = 5 }: UseUndoableChangesOptions<K, V>) {
  const [pending, setPending] = useState<Map<K, PendingChange<V>>>(new Map());
  const timers = useRef(new Map<K, Timer<V>>());
  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const setSecondsLeft = useCallback((key: K, change: PendingChange<V> | undefined) => {
    setPending((prev) => {
      const next = new Map(prev);
      if (change) {
        next.set(key, change);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const stop = useCallback((key: K) => {
    const timer = timers.current.get(key);
    if (timer) {
      clearInterval(timer.interval);
      timers.current.delete(key);
    }
  }, []);

  const undo = useCallback(
    (key: K) => {
      stop(key);
      setSecondsLeft(key, undefined);
    },
    [stop, setSecondsLeft]
  );

  const schedule = useCallback(
    (key: K, value: V) => {
      stop(key);
      const timer: Timer<V> = {
        value,
        remaining: seconds,
        interval: setInterval(() => {
          timer.remaining -= 1;
          if (timer.remaining > 0) {
            setSecondsLeft(key, { value, secondsLeft: timer.remaining });
            return;
          }
          stop(key);
          setSecondsLeft(key, undefined);
          onCommitRef.current(key, value);
        }, 1000),
      };
      timers.current.set(key, timer);
      setSecondsLeft(key, { value, secondsLeft: seconds });
    },
    [seconds, stop, setSecondsLeft]
  );

  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const [key, timer] of live) {
        clearInterval(timer.interval);
        onCommitRef.current(key, timer.value);
      }
      live.clear();
    };
  }, []);

  return { pending, schedule, undo };
}
